// src/services/imageGenerationApi.ts

import { getApiUrl } from '../config/api';

// Global limit exceeded callback - set by LimitExceededContext
let limitExceededCallback: ((message: string) => void) | null = null;

export const setLimitExceededCallback = (callback: (message: string) => void) => {
  limitExceededCallback = callback;
};

export interface GenerateImageRequest {
  prompt: string;
  projectId?: string;
  provider?: 'runway' | 'sdxl' | 'vertex_gemini' | 'banana_nano'; // Updated to include new providers
  
  // Existing SDXL params
  steps?: number;
  guidance_scale?: number;
  width?: number;
  height?: number;
  seed?: number;
  negative_prompt?: string;
  use_refiner?: boolean;
  refiner_steps?: number;
  count?: number;
  
  // Runway-specific params (from API docs)
  runway_ratio?: string;           // Image dimensions like "1280:720"
  runway_model?: string;           // Default: 'gen4_image'
  num_images?: number;             // 1-4 images per generation
  reference_images?: ReferenceImage[]; // Style/content references
  
  // Vertex Gemini specific params
  vertex_ratio?: string;           // "1:1", "16:9", "9:16", "4:3", "3:4"
  vertex_model?: string;           // Default: 'imagen-3.0-generate-001'
  person_generation?: 'dont_allow' | 'allow_adult' | 'allow_all';
  safety_filter_level?: 'block_most' | 'block_some' | 'block_few' | 'block_fewest';
  
  // Banana Nano specific params (via Vertex AI)
  banana_guidance_scale?: number;  // Guidance scale for generation
  banana_num_inference_steps?: number; // Number of inference steps
  banana_scheduler?: 'DPMSolverMultistepScheduler' | 'EulerAncestralDiscreteScheduler' | 'EulerDiscreteScheduler' | 'DDIMScheduler';
  
  // Backward compatibility
  aspect_ratio?: string;           // Maps to runway_ratio
}

export interface ReferenceImage {
  uri: string;                     // Image URL or data URI
  url?: string;                    // Alias for uri (backward compatibility)
  name?: string;                   // Optional name for the image
  uploadedAt?: string;             // Upload timestamp
  tag?: string;                    // For @mention in prompts
}

export interface GenerateImageResponse {
  success: boolean;
  message: string;
  data: {
    projectId: string;
    s3Url?: string; // Single image URL
    images?: Array<{ // Multiple images (SDXL batch)
      s3Url: string;
      seed: number;
      index: number;
      prompt: string;
    }>;
    seed: number;
    prompt: string;
    provider?: string;            // NEW: Provider used
    cost?: number;               // NEW: Generation cost
    runway_task_id?: string;     // NEW: Runway task ID
    num_images_generated?: number; // NEW: Total images generated
    reference_images_used?: number; // NEW: Reference images count
  };
}

// Provider configuration
export const PROVIDER_CONFIG = {
  runway: {
    endpoint: '/image-generation/single', // Same endpoint, provider param differentiates
    default: true,
    maxImages: 4, // Updated based on API docs
    supportedRatios: ['1280:720', '1584:672', '1104:832', '720:1280', '832:1104', '960:960'],
    features: ['reference_images', 'style_transfer', 'high_quality']
  },
  sdxl: {
    endpoint: '/image-generation/batch', // Same endpoint, provider param differentiates  
    default: false,
    maxImages: 4,
    supportedRatios: ['16:9', '9:16', '1:1', '4:3', '3:2'],
    features: ['negative_prompts', 'refiner', 'batch_generation']
  },
  vertex_gemini: {
    endpoint: '/image-generation/single',
    default: false,
    maxImages: 4,
    supportedRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    features: ['reference_images', 'safety_filters', 'person_generation']
  },
  banana_nano: {
    endpoint: '/image-generation/single',
    default: false,
    maxImages: 4,
    supportedRatios: ['1:1', '16:9', '9:16', '4:3', '3:2'], // Standard pixel ratios
    features: ['negative_prompts', 'schedulers', 'inference_steps']
  }
} as const;

export const generateImage = async (request: GenerateImageRequest): Promise<GenerateImageResponse> => {
  const provider = request.provider || 'runway'; // Default to runway (frontend choice)
  const config = PROVIDER_CONFIG[provider as keyof typeof PROVIDER_CONFIG];
  
  // Add logging for API call parameters
  console.log('🎨 IMAGE GENERATION API CALL PARAMS:', {
    provider: provider,
    prompt: request.prompt,
    projectId: request.projectId,
    // Provider-specific params
    ...(provider === 'runway' && {
      runway_ratio: request.runway_ratio,
      runway_model: request.runway_model,
      num_images: request.num_images,
      reference_images: request.reference_images?.length || 0
    }),
    ...(provider === 'sdxl' && {
      width: request.width,
      height: request.height,
      steps: request.steps,
      guidance_scale: request.guidance_scale,
      use_refiner: request.use_refiner
    }),
    ...(provider === 'vertex_gemini' && {
      vertex_ratio: request.vertex_ratio,
      vertex_model: request.vertex_model,
      person_generation: request.person_generation,
      safety_filter_level: request.safety_filter_level,
      reference_images: request.reference_images?.length || 0
    }),
    ...(provider === 'banana_nano' && {
      width: request.width,
      height: request.height,
      banana_guidance_scale: request.banana_guidance_scale,
      banana_num_inference_steps: request.banana_num_inference_steps,
      banana_scheduler: request.banana_scheduler,
      negative_prompt: request.negative_prompt
    })
  });
  
  const response = await fetch(getApiUrl(config.endpoint), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('authToken') && {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      })
    },
    body: JSON.stringify({
      ...request,
      provider // Ensure provider is sent to backend
    }),
  });

  if (!response.ok) {
    // Parse error response body for detailed error info
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      // If parsing fails, throw generic error
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check for 403 limit exceeded error
    if (response.status === 403 && errorData?.message) {
      // Check if message contains limit exceeded keywords
      if (errorData.message.toLowerCase().includes('limit exceeded') || 
          errorData.message.toLowerCase().includes('image_generation')) {
        
        // Trigger the global limit modal if callback is set
        if (limitExceededCallback) {
          limitExceededCallback(errorData.message);
        }
        
        // Still throw error so component can handle it if needed
        const error = new Error(errorData.message);
        (error as any).statusCode = 403;
        (error as any).isLimitExceeded = true;
        throw error;
      }
    }

    // For other errors, throw with message if available
    throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  console.log('📥 IMAGE GENERATION API RESPONSE:', {
    success: result.success,
    provider: result.data?.provider,
    projectId: result.data?.projectId,
    s3Url: result.data?.s3Url ? 'Present' : 'Missing',
    images_count: result.data?.images?.length || 0,
    cost: result.data?.cost,
    message: result.message
  });

  return result;
};

// Runway-specific style presets
export const RUNWAY_STYLE_PRESETS = [
  {
    name: 'Photorealistic',
    value: 'photorealistic, high detail, professional photography',
    category: 'Photography'
  },
  {
    name: 'Cinematic',
    value: 'cinematic lighting, film photography, dramatic composition',
    category: 'Film'
  },
  {
    name: 'Fashion Portrait',
    value: 'fashion photography, studio lighting, high fashion, editorial',
    category: 'Portrait'
  },
  {
    name: 'Product Shot',
    value: 'product photography, clean background, professional lighting',
    category: 'Commercial'
  },
  {
    name: 'Artistic',
    value: 'artistic composition, creative lighting, fine art photography',
    category: 'Art'
  },
  {
    name: 'Street Photography',
    value: 'street photography, candid, urban environment, natural lighting',
    category: 'Documentary'
  }
];

// Vertex Gemini style presets
export const VERTEX_GEMINI_STYLE_PRESETS = [
  {
    name: 'Professional Photo',
    value: 'professional photography, studio lighting, high resolution, sharp focus',
    category: 'Photography'
  },
  {
    name: 'Creative Art',
    value: 'creative artistic style, unique composition, artistic vision, detailed',
    category: 'Art'
  },
  {
    name: 'Portrait Style',
    value: 'portrait photography, professional lighting, detailed facial features',
    category: 'Portrait'
  },
  {
    name: 'Landscape',
    value: 'landscape photography, natural lighting, scenic view, high detail',
    category: 'Nature'
  },
  {
    name: 'Abstract Art',
    value: 'abstract art, creative composition, vibrant colors, artistic interpretation',
    category: 'Abstract'
  }
];

// Banana Nano style presets
export const BANANA_NANO_STYLE_PRESETS = [
  {
    name: 'Realistic',
    value: 'photorealistic, high detail, sharp focus, professional quality',
    category: 'Photography'
  },
  {
    name: 'Artistic',
    value: 'artistic style, creative composition, vibrant colors, detailed illustration',
    category: 'Art'
  },
  {
    name: 'Fantasy',
    value: 'fantasy art, magical, ethereal, detailed illustration, mystical',
    category: 'Fantasy'
  },
  {
    name: 'Cyberpunk',
    value: 'cyberpunk style, neon lights, futuristic, high tech, sci-fi',
    category: 'Sci-Fi'
  },
  {
    name: 'Anime Style',
    value: 'anime style, manga art, detailed character design, vibrant colors',
    category: 'Anime'
  },
  {
    name: 'Oil Painting',
    value: 'oil painting style, classical art, brushstrokes, artistic texture',
    category: 'Classical'
  }
];

// Predefined style presets for better prompts (SDXL-focused)
export const STYLE_PRESETS = [
  {
    name: 'Pixar 3D',
    value: 'pixar 3d, animated, colorful, cartoon style, high quality render',
    category: 'Animation'
  },
  {
    name: 'Realistic Photo',
    value: 'realistic, photographic, high resolution, detailed, professional photography',
    category: 'Photography'
  },
  {
    name: 'Oil Painting', 
    value: 'oil painting, artistic, classical art style, brushstrokes, fine art',
    category: 'Art'
  },
  {
    name: 'Digital Art',
    value: 'digital art, concept art, detailed, vibrant colors, professional illustration',
    category: 'Digital'
  },
  {
    name: 'Watercolor',
    value: 'watercolor painting, soft colors, artistic, hand-painted style',
    category: 'Art'
  },
  {
    name: 'Minimalist',
    value: 'minimalist, clean, simple, modern design, white background',
    category: 'Design'
  }
];

// Aspect ratio presets
export const ASPECT_RATIOS = [
  { name: '16:9 (Landscape)', width: 1536, height: 864 },
  { name: '9:16 (Portrait)', width: 864, height: 1536 },
  { name: '1:1 (Square)', width: 1024, height: 1024 },
  { name: '4:3 (Standard)', width: 1344, height: 1008 },
  { name: '3:2 (Photo)', width: 1536, height: 1024 },
];

// Runway aspect ratios (using standard ratios as keys)
export const RUNWAY_RATIOS = {
  "16:9": "Landscape HD",
  // "21:9": "Ultra-wide landscape",  onlybecause we use veo as generationon the backend
  // "4:3": "Landscape standard",
  "9:16": "Portrait HD",
  // "3:4": "Portrait standard",
  // "1:1": "Square"
};

// Convert SDXL ratios to Runway ratios
export const mapAspectRatioToRunway = (aspectRatio: string): string => {
  const mapping: Record<string, string> = {
    '16:9': '1280:720',
    '21:9': '1584:672',
    '9:16': '720:1280', 
    '1:1': '960:960',
    '4:3': '1104:832',
    '3:4': '832:1104',
    '3:2': '1280:720' // Closest match
  };
  return mapping[aspectRatio] || '1280:720';
};

// Convert Runway pixel ratios back to standard ratios
export const mapRunwayRatioToStandard = (runwayRatio: string): string => {
  const reverseMapping: Record<string, string> = {
    '1280:720': '16:9',
    '1584:672': '21:9',
    '720:1280': '9:16',
    '960:960': '1:1',
    '1104:832': '4:3',
    '832:1104': '3:4'
  };
  
  // If it's already a standard ratio, return it
  if (['16:9', '21:9', '9:16', '1:1', '4:3', '3:4', 'custom'].includes(runwayRatio)) {
    return runwayRatio;
  }
  
  return reverseMapping[runwayRatio] || '16:9';
};

// Utility functions for provider management
export const getProviderConfig = (provider: 'runway' | 'sdxl' | 'vertex_gemini' | 'banana_nano' = 'runway') => {
  return PROVIDER_CONFIG[provider];
};

export const getStylePresets = (provider: 'runway' | 'sdxl' | 'vertex_gemini' | 'banana_nano' = 'runway') => {
  switch(provider) {
    case 'runway': return RUNWAY_STYLE_PRESETS;
    case 'vertex_gemini': return VERTEX_GEMINI_STYLE_PRESETS;
    case 'banana_nano': return BANANA_NANO_STYLE_PRESETS;
    case 'sdxl':
    default: return STYLE_PRESETS; // SDXL presets
  }
};

export const getMaxImages = (provider: 'runway' | 'sdxl' | 'vertex_gemini' | 'banana_nano' = 'runway') => {
  return PROVIDER_CONFIG[provider].maxImages;
};

export const getSupportedRatios = (provider: 'runway' | 'sdxl' | 'vertex_gemini' | 'banana_nano' = 'runway') => {
  if (provider === 'runway') {
    return Object.entries(RUNWAY_RATIOS).map(([ratio, name]) => ({
      name: `${ratio} (${name})`,
      ratio: ratio,
      value: ratio, // For consistent access
      width: parseInt(ratio.split(':')[0]),
      height: parseInt(ratio.split(':')[1])
    }));
  } else {
    const config = PROVIDER_CONFIG[provider];
    const supportedRatioKeys = [...config.supportedRatios] as string[];
    return ASPECT_RATIOS.filter(ratio => 
      supportedRatioKeys.includes(ratio.name.split(' ')[0])
    ).map(ratio => ({
      name: ratio.name,
      ratio: ratio.name.split(' ')[0], // Extract ratio part
      value: ratio.name.split(' ')[0], // For consistent access
      width: ratio.width,
      height: ratio.height
    }));
  }
};

export const hasFeature = (
  provider: 'runway' | 'sdxl' | 'vertex_gemini' | 'banana_nano', 
  feature: 'reference_images' | 'style_transfer' | 'high_quality' | 'negative_prompts' | 'refiner' | 'batch_generation' | 'safety_filters' | 'person_generation' | 'schedulers' | 'inference_steps'
) => {
  return [...PROVIDER_CONFIG[provider].features].includes(feature);
};

// Build request parameters based on provider
export const buildProviderParams = (
  provider: 'runway' | 'sdxl' | 'vertex_gemini' | 'banana_nano',
  baseParams: any,
  aspectRatio: string
): Partial<GenerateImageRequest> => {
  if (provider === 'runway') {
    return {
      ...baseParams,
      provider: 'runway',
      runway_ratio: mapAspectRatioToRunway(aspectRatio),
      runway_model: 'gen4_image',
      num_images: baseParams.count || 4,
      // Map reference_images if provided
      ...(baseParams.reference_images && {
        reference_images: baseParams.reference_images
      })
    };
  } else if (provider === 'vertex_gemini') {
    return {
      ...baseParams,
      provider: 'banana_nano',
      vertex_ratio: aspectRatio, // Direct mapping to standard ratios
      vertex_model: baseParams.vertex_model || 'imagen-3.0-generate-001',
      person_generation: baseParams.person_generation || 'allow_adult',
      safety_filter_level: baseParams.safety_filter_level || 'block_some',
      num_images: baseParams.count || 1,
      // Map reference_images if provided
      ...(baseParams.reference_images && {
        reference_images: baseParams.reference_images
      })
    };
  } else if (provider === 'banana_nano') {
    const dimensions = ASPECT_RATIOS.find(ar => ar.name.startsWith(aspectRatio));
    return {
      ...baseParams,
      provider: 'banana_nano',
      width: dimensions?.width || 1024,
      height: dimensions?.height || 1024,
      banana_guidance_scale: baseParams.banana_guidance_scale || 7.5,
      banana_num_inference_steps: baseParams.banana_num_inference_steps || 20,
      banana_scheduler: baseParams.banana_scheduler || 'DPMSolverMultistepScheduler',
      negative_prompt: baseParams.negative_prompt || 'blurry, low quality, distorted, ugly',
      num_images: baseParams.count || 1
    };
  } else {
    // SDXL parameters
    const dimensions = ASPECT_RATIOS.find(ar => ar.name.startsWith(aspectRatio));
    return {
      ...baseParams,
      provider: 'sdxl',
      width: dimensions?.width || 1536,
      height: dimensions?.height || 864,
      count: baseParams.count || 4,
      // Map reference_images if provided
      ...(baseParams.reference_images && {
        reference_images: baseParams.reference_images
      })
    };
  }
};
